import { create } from 'zustand';
import axiosClient from '../services/api';

const useAssignmentStore = create((set, get) => ({
  assignment: null,
  submissions: [],
  submission: null,
  isLoading: false,
  error: null,


  fetchAssignment: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/learning/assignments/${id}`);
      set({ assignment: response.data.assignment, isLoading: false });
      return response.data.assignment;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch assignment', isLoading: false });
      return null;
    }
  },

  fetchAssignmentForStudent: async (assignmentId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/learning/assignments/${assignmentId}/student`);
      set({ assignment: response.data.assignment, isLoading: false });
      return response.data.assignment;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch assignment',
        isLoading: false
      });
      return null;
    }
  },


  fetchMySubmission: async (assignmentId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/learning/assignments/${assignmentId}/my-submission`);
      set({ submission: response.data.data, isLoading: false });
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 404) {
        set({ submission: null, isLoading: false });
        return null;
      }
      set({
        error: error.response?.data?.error || 'Failed to fetch your submission',
        isLoading: false
      });
      return null;
    }
  },

  // Create a new assignment
  createAssignment: async (assignmentData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post('/learning/assignments', assignmentData);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to create assignment', isLoading: false });
      return null;
    }
  },

  // Update an existing assignment
  updateAssignment: async (id, assignmentData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.put(`/learning/assignments/update/${id}`, assignmentData);
      set({ assignment: response.data.assignment, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to update assignment', isLoading: false });
      return null;
    }
  },

  deleteAssignment: async (assignmentId) => {
    set({ isLoading: true, error: null });
    try {
      await axiosClient.delete(`/learning/assignments/delete/${assignmentId}`);
      set({ isLoading: false });
      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete assignment', isLoading: false });
      return null;
    }
  },

  // Fetch all submissions for an assignment
  fetchSubmissions: async (assignmentId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/learning/assignments/${assignmentId}/submissions`);
      set({ submissions: response.data.data, isLoading: false });
      return response.data.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch submissions', isLoading: false });
      return null;
    }
  },

  // Fetch a single submission by ID
  fetchSubmission: async (submissionId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/learning/assignments/submissions/${submissionId}`);
      set({ submission: response.data.data, isLoading: false });
      return response.data.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch submission', isLoading: false });
      return null;
    }
  },

  submitAssignment: async (assignmentId, formData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post(`/learning/assignments/${assignmentId}/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      set({ submission: response.data.submission, isLoading: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to submit assignment',
        isLoading: false
      });
      return null;
    }
  },

  // Grade a submission
  gradeSubmission: async (submissionId, gradeData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.put(`/learning/assignments/submissions/${submissionId}`, gradeData);
      // Optimistically update submissions list
      const updatedSubmissions = get().submissions.map(sub =>
        sub._id === submissionId ? { ...sub, ...response.data.data, gradedAt: new Date() } : sub
      );
      set({ submissions: updatedSubmissions, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to grade submission', isLoading: false });
      return null;
    }
  },

  // Clear error
  clearError: () => set({ error: null })
}));

export default useAssignmentStore;